// DB Service Thunks
import {
    botSelectOneObjectByQuery,
    DBBotDeploymentThunks,
    DBBotThunks
} from '@common-reducers/DBServiceThunks';

// Redux Hooks and Thunks
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { setIsReadyToLoadFlow, setSlugBotId } from '@common/reducers/ChatbotReducer';
import { getEffectiveDeploymentIdSelector } from '@common/reducers/ChatbotSelectors';
import { useCalculateBotStatsMutation } from '@common/reducers/backend-api/backend-api';

// React and Router
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@common/hooks/use-is-mobile';

// Components
import AppView from '@common/components/app-view';
import { BotStats } from './components/BotStats';
import { DeploymentsSidebar } from './components/DeploymentsSidebar';
import { SessionsList } from './components/SessionsList';
import GoogleTranslateErrorBoundary from '@common/components/google-translate-error-boundary/google-translate-error-boundary';
import { namespace } from '@common-config/constants';
import { botDetailGateState } from './bot-detail-gate';

// Shadcn UI Components
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

// Lucide Icons
import { ArrowLeft, Edit, Loader2, MessageSquare } from 'lucide-react';

type SessionFilter = 'all' | 'active' | 'inactive';

export const BotDetailView = () => {
    const { t } = useTranslation(namespace);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { botId } = useParams<{ botId: string }>();
    const isMobile = useIsMobile();
    const [sessionFilter, setSessionFilter] = useState<SessionFilter>('all');
    const [manuallySelectedDeploymentId, setManuallySelectedDeploymentId] = useState<string | null>(null);
    // Whether THIS view's own fetch for the current botId has settled. The page
    // gate keys off this + bot presence — NOT the global DBBot*Reducer loading
    // flags, which unrelated shell fetches toggle (see #433 / bot-detail-gate).
    const [initialFetchDone, setInitialFetchDone] = useState(false);
    const [calculateBotStats] = useCalculateBotStatsMutation();

    // Google Translate DOM Protection
    useEffect(() => {
        // Add notranslate class to prevent Google Translate from modifying this component
        const element = document.querySelector('.bot-detail-view');
        if (element) {
            element.classList.add('notranslate');
        }
        
        // Also add to the root element
        const rootElement = document.getElementById('root');
        if (rootElement) {
            rootElement.classList.add('notranslate');
        }
    }, []);

    // Bot data for THIS botId. The page gate deliberately does NOT read the
    // global DBBotReducer/DBBotDeploymentReducer `itemsLoading` flags: those are
    // toggled by unrelated shell fetches (the sidebar's all-bots hasLegacyBots
    // fetch), and gating the whole page on them caused the #433 infinite
    // remount/refetch loop (spinner forever).
    const bot = useAppSelector(state => botSelectOneObjectByQuery(state.DBBotReducer, { _id: botId }));

    // Use the selector to get the effective deployment ID
    const selectedDeploymentId = useAppSelector<string | null>(state =>
        botId ? (getEffectiveDeploymentIdSelector(state, botId, manuallySelectedDeploymentId) as string | null) : null
    );

    const handleFilterChange = (newFilter: SessionFilter) => {
        setSessionFilter(newFilter);
    };

    const fetchBotData = useCallback(async (botId: string) => {
        console.log('Fetching bot data for botId:', botId);
        await dispatch(setSlugBotId(botId));

        console.log('Fetching bot details for botId:', botId);
        const botResult = await dispatch(DBBotThunks.find({
            _id: botId,
            $paginate: false
        }));
        console.log('Bot details result:', botResult);

        // Find deployments for this bot
        console.log('Fetching deployments for botId:', botId);
        const deploymentResult = await dispatch(DBBotDeploymentThunks.find({
            botId: botId,
            $paginate: false
        }));
        console.log('Deployments result:', deploymentResult);

        // Calculate bot stats
        if (botId) {
            try {
                await calculateBotStats([botId]);
            } catch (error) {
                console.error('Failed to calculate bot stats:', error);
            }
        }

        console.log('Finished fetching data for botId:', botId);
    }, [dispatch, calculateBotStats]);

    // Effect to fetch the bot data when botId changes
    useEffect(() => {
        if (!botId) return;
        let cancelled = false;
        setInitialFetchDone(false);
        dispatch(setSlugBotId(botId));
        fetchBotData(botId)
            .catch(() => { /* swallowed: the gate falls through to not-found */ })
            .finally(() => { if (!cancelled) setInitialFetchDone(true); });
        return () => { cancelled = true; };
    }, [botId, dispatch, fetchBotData]);

    const handleEditClick = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (bot?._id) {
            await dispatch(setIsReadyToLoadFlow(false));
            await navigate(`/bot-build-view/${bot._id}`);
        }
    };

    const handleBackClick = () => {
        navigate('/bot-list-view');
    };

    // Single render shape across all states. The AppView (and its shell) is
    // mounted at ONE fixed tree position wrapped in the same error boundary for
    // loading, not-found and ready alike — so flipping between states can never
    // remount the shell and retrigger its all-bots fetch (the #433 loop).
    const gate = botDetailGateState({ bot, initialFetchDone });

    const loadingContent = (
        <div className="flex justify-center items-center h-full w-full">
            <Loader2
                className="h-8 w-8 animate-spin"
                style={{ color: 'var(--bot-detail-loading-icon)' }}
            />
        </div>
    );

    const notFoundContent = (
        <div style={{
            minHeight: '100vh',
            backgroundColor: 'var(--bot-detail-bg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        }}>
            <div style={{ maxWidth: '448px', textAlign: 'center' }}>
                <h2 style={{
                    fontSize: '1.5rem',
                    fontWeight: '600',
                    color: 'var(--bot-detail-error-title)',
                    marginBottom: '8px',
                }}>
                    {t('chatbot.botDetail.agentNotFoundTitle')}
                </h2>
                <p style={{
                    color: 'var(--bot-detail-error-text)',
                    marginBottom: '24px',
                }}>
                    {t('chatbot.botDetail.agentNotFoundDescription')}
                </p>
                <Button
                    onClick={handleBackClick}
                    style={{
                        backgroundColor: 'var(--bot-detail-button-primary)',
                        color: 'var(--bot-detail-button-primary-text)',
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--bot-detail-button-primary-hover)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--bot-detail-button-primary)';
                    }}
                >
                    {t('chatbot.botDetail.backToAgents')}
                </Button>
            </div>
        </div>
    );

    return (
        <GoogleTranslateErrorBoundary>
            <AppView title={t('chatbot.botDetail.pageTitle')} showHeader={false}>
                {!bot ? (gate === 'loading' ? loadingContent : notFoundContent) : (
                <div className="bot-detail-view" style={{
                    minHeight: '100vh',
                    backgroundColor: 'var(--bot-detail-bg)',
                }}>
                    <div className={`p-4 md:p-6 flex flex-col ${isMobile ? 'min-h-[calc(100vh-48px)]' : 'h-[calc(100vh-48px)]'}`}>
                        {/* Header */}
                        <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
                            <div className="flex items-center min-w-0">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={handleBackClick}
                                    className="mr-2"
                                    style={{
                                        color: 'var(--bot-detail-button-ghost)',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.color = 'var(--bot-detail-button-ghost-hover)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.color = 'var(--bot-detail-button-ghost)';
                                    }}
                                >
                                    <ArrowLeft className="h-5 w-5" />
                                </Button>
                                <h1 className="truncate" style={{
                                    fontSize: '1.5rem',
                                    fontWeight: '600',
                                    color: 'var(--bot-detail-title)',
                                }}>
                                    {bot.name}
                                </h1>
                            </div>
                            <Button
                                onClick={handleEditClick}
                                size="sm"
                                style={{
                                    backgroundColor: 'var(--bot-detail-button-primary)',
                                    color: 'var(--bot-detail-button-primary-text)',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'var(--bot-detail-button-primary-hover)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'var(--bot-detail-button-primary)';
                                }}
                            >
                                <Edit className="h-4 w-4 mr-2" />
                                {t('chatbot.botDetail.editAgent')}
                            </Button>
                            <Button
                                onClick={() => bot?._id && navigate(`/bot-test-chat/${bot._id}`)}
                                size="sm"
                                variant="outline"
                                disabled={!bot?._id}
                                style={{ marginLeft: 8 }}
                            >
                                <MessageSquare className="h-4 w-4 mr-2" />
                                Start Test Conversation
                            </Button>
                        </div>

                        {/* Stats */}
                        <div className="mb-6">
                            <BotStats stats={bot.botStats} />
                        </div>

                        {/* Content Container */}
                        <div className={`flex gap-6 flex-1 ${isMobile ? 'flex-col overflow-visible' : 'overflow-hidden'}`}>
                            {/* Sessions List */}
                            <Card style={{
                                flex: 1,
                                border: '1px solid var(--bot-detail-card-border)',
                                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                                overflow: 'hidden',
                                backgroundColor: 'var(--bot-detail-card-bg)',
                            }}>
                                <SessionsList
                                    filter={sessionFilter}
                                    onSessionFilterChange={handleFilterChange}
                                    selectedDeploymentId={selectedDeploymentId}
                                    onDeploymentSelect={setManuallySelectedDeploymentId}
                                />
                            </Card>

                            {/* Deployments List */}
                            <Card style={{
                                width: isMobile ? '100%' : '320px',
                                border: '1px solid var(--bot-detail-card-border)',
                                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                                overflow: 'hidden',
                                backgroundColor: 'var(--bot-detail-card-bg)',
                            }}>
                                <DeploymentsSidebar
                                    botId={botId}
                                    selectedDeploymentId={selectedDeploymentId}
                                    onDeploymentSelect={setManuallySelectedDeploymentId}
                                />
                            </Card>
                        </div>
                    </div>
                </div>
                )}
            </AppView>
        </GoogleTranslateErrorBoundary>
    );
};
